import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { Address, IFormModel, IFormSelect, IOlavEntry } from '@mwe/models';
import { CountryLogic, getFromOlavKey } from '@mwe/utils';
import { FormComponent } from '../form/form.component';
import { ManualAddressInputComponent } from './manual-address-input.component';
import { AddressSearchWrapperComponent } from '../address-search-wrapper/address-search-wrapper';

@Component({
  selector: 'mwe-international-address-input',
  templateUrl: './international-address-input.component.html',
  imports: [FormComponent, ManualAddressInputComponent, AddressSearchWrapperComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternationalAddressInputComponent implements OnInit, AfterViewInit {
  private readonly countryLogic = inject(CountryLogic);

  private readonly countryFormRef = viewChild(FormComponent);
  private readonly addressInputRef = viewChild(AddressSearchWrapperComponent);
  private readonly manualAddressInputRef = viewChild(ManualAddressInputComponent);
  private baseCountry = signal('AT');

  readonly selectionFinishedEvent = output<boolean>();

  initialOlavEntry = input<IOlavEntry>();
  initialDataForManualForm = input<Address>();

  useManualAddressForm = linkedSignal(() => !this.initialOlavEntry() && !!this.initialDataForManualForm());
  manualAddress = linkedSignal(() => this.initialDataForManualForm());

  countryFormModel = signal<IFormModel>(null);

  private readonly countrySelector = 'manual-address-country';

  ngOnInit() {
    this.createFormModel();
  }

  ngAfterViewInit(): void {
    this.onChanges();
    this.getCountryFormRef()?.fireValidation();
  }

  validate(): boolean {
    this.getCountryFormRef().fireValidation();

    if (this.getCountryFormRef().form.invalid) {
      return false;
    }

    if (this.useManualAddressForm()) {
      return this.manualAddressInputRef().validate();
    }

    return this.addressInputRef().validate();
  }

  getOlavEntry(): IOlavEntry {
    if (this.useManualAddressForm()) {
      return undefined;
    }

    return this.addressInputRef()?.selectedOlav();
  }

  getAddress() {
    const address = this.useManualAddressForm()
      ? this.manualAddressInputRef().getAddressFromForm()
      : this.getFromOlavKey(this.addressInputRef().selectedOlav());

    if (!address) {
      return null;
    }

    return {
      ...address,
      country: this.getCountryFormControlValue()?.value,
    };
  }

  onChanges() {
    const value = this.getCountryFormControlValue()?.value;
    if (!value) return;
    this.baseCountry.set(value);
    this.useManualAddressForm.set(value !== 'AT' && !!value);
  }

  changeToManualAddressForm(): void {
    this.manualAddressInputRef()?.formRef()?.reset();
    this.manualAddress.set(this.getFromOlavKey(this.addressInputRef().activeFormManualEntry()));
    this.useManualAddressForm.set(true);
  }

  getCountryFormRef(): FormComponent {
    return this.countryFormRef();
  }

  receiveSelectionFinished($event: boolean): void {
    this.selectionFinishedEvent.emit($event);
  }

  private createFormModel(): void {
    const initialCountryCode = this.manualAddress()?.country || this.baseCountry();

    this.countryFormModel.set({
      updateOn: 'blur',
      inputs: [
        {
          name: this.countrySelector,
          initialValue: initialCountryCode,
          validators: [Validators.required],
          labelKey: 'shared.international-address-input.form.country.title',
          validationErrorLabelKey: 'shared.international-address-input.form.country.error',
          validate: true,
          componentType: 'typeahead',
          validateSuccess: true,
          options: this.countryLogic.getCountryCodeValues(),
          typeaheadOptions: {
            initialSuggestions: this.countryLogic.getCountryCodeInitialSuggestions(),
          },
        },
      ],
    });
  }

  private getCountryFormControlValue(): IFormSelect {
    return this.getCountryFormRef()?.form?.get(this.countrySelector)?.value as IFormSelect;
  }

  private getFromOlavKey(olav: IOlavEntry): Address {
    return getFromOlavKey(olav);
  }
}
